// M2 ads level for the LO Ads Dashboard page (gap G10, design rev
// AlaudaAI/contexts #18 D4/D9): the currently-running ads as a Google Ads
// style table under the campaign summary. Every row rides in the single
// dashboard response; nothing in this section ever triggers a request (D9).
//
// The metric columns stay pinned to the FIXED 30-day window: there is no
// per-ad daily series upstream (per-ad range slicing was rejected, design
// (e)), so the column heads say "Last 30 days" and the table deliberately
// ignores the page's date range selector.
//
// Creative fields are render-guarded: copy falls back to the ad name, the
// thumbnail renders ONLY an https imageUrl (same scheme check as previews,
// with an onError fallback) and shows a quiet placeholder otherwise. Preview
// is a per-ad https click-out: a null/unsafe previewUrl leaves the cell
// blank, an expired or unparseable expiry degrades to "being refreshed" for
// that ad alone — never a dead link. The tri-state ads field (rows / [] /
// null-or-missing) is interpreted here; a degraded or malformed ads level
// quiets THIS SECTION only while the campaign summary above stays
// authoritative and untouched.

import React, { useState } from "react";

import {
  interpretAdsSection,
  derivePreviewState,
  isSafePreviewUrl,
  splitAdCopy,
  type AdsAdRow,
} from "./ads-model";

function formatCount(n: number): string {
  return Number.isFinite(n) ? Math.round(n).toLocaleString() : "N/A";
}

// Same percent treatment as the campaign tiles; a null per-ad CTR (zero
// impressions) renders as a quiet placeholder rather than a rate.
function formatCtr(v: number | null): string {
  return v == null || !Number.isFinite(v) ? "—" : `${(v * 100).toFixed(2)}%`;
}

function AdThumb({ ad }: { ad: AdsAdRow }) {
  const [failed, setFailed] = useState(false);
  const src = ad.imageUrl ?? null;
  // FAIL CLOSED: only a clean https URL ever reaches an img src; anything
  // else (missing, null, http, malformed, or a load error) is the placeholder.
  if (failed || src === null || !isSafePreviewUrl(src)) {
    return <span className="ads-thumb ads-thumb-placeholder" data-testid="ads-thumb-placeholder" aria-hidden="true" />;
  }
  return (
    <img
      className="ads-thumb"
      src={src}
      alt=""
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

function AdPreviewCell({ ad, now }: { ad: AdsAdRow; now: Date }) {
  const preview = derivePreviewState(ad.previewUrl, ad.previewExpiresAt, now);
  if (preview.kind === "hidden") {
    return null;
  }
  if (preview.kind === "refreshing") {
    return (
      <span className="ads-muted" data-testid="ads-preview-refreshing">
        This ad&apos;s preview is being refreshed
      </span>
    );
  }
  return (
    <a
      className="ads-preview-link"
      data-testid="ads-preview-link"
      href={preview.url}
      target="_blank"
      rel="noopener noreferrer"
    >
      View
    </a>
  );
}


function AdTableRow({ ad, now }: { ad: AdsAdRow; now: Date }) {
  // The copy HEADLINE (first line, per the api-ads "headline\n\nbody"
  // convention) is the main line and the full copy stays on the tooltip; the
  // name drops to the secondary line. Rows from before the backend grew
  // copy/imageUrl (or with nulls) fall back to the name, then to the
  // untitled label.
  const headline = ad.copy != null ? splitAdCopy(ad.copy).headline : "";
  const mainLine = headline !== "" ? headline : ad.name ?? "Untitled ad";
  const secondary = headline !== "" && ad.name != null ? ad.name : null;
  return (
    <tr className="ads-ad-row" data-testid="ads-ad-row">
      <td className="ads-td-thumb">
        <AdThumb ad={ad} />
      </td>
      <td className="ads-td-ad">
        <div className="ads-ad-copy" title={ad.copy ?? undefined}>
          {mainLine}
        </div>
        {secondary ? <div className="ads-ad-name">{secondary}</div> : null}
      </td>
      <td className="ads-td-status">
        {/* Only "active" rows survive validation (contract: running ads only). */}
        <span className="ads-status-dot ads-status-dot-active" aria-hidden="true" />
        <span className="ads-ad-status-text">{ad.status}</span>
      </td>
      <td className="ads-td-num">{formatCount(ad.impressions)}</td>
      <td className="ads-td-num">{formatCount(ad.clicks)}</td>
      <td className="ads-td-num">{formatCtr(ad.ctr)}</td>
      <td className="ads-td-preview">
        <AdPreviewCell ad={ad} now={now} />
      </td>
    </tr>
  );
}

/**
 * The "Running ads" section below the campaign summary. `ads` arrives RAW
 * from the dashboard body; interpretAdsSection re-validates it here so a
 * malformed ads level can never blank the page above.
 */
export default function AdsSection({ ads }: { ads: unknown }) {
  const section = interpretAdsSection(ads);
  const now = new Date();

  return (
    <section className="ads-section" data-testid="ads-ads-section">
      <h2 className="ads-section-heading">Running ads</h2>
      {section.kind === "unavailable" ? (
        <p className="ads-muted" data-testid="ads-ads-unavailable">
          Ad details are temporarily unavailable.
        </p>
      ) : null}
      {section.kind === "empty" ? (
        <p className="ads-muted" data-testid="ads-ads-empty">
          No ads currently running.
        </p>
      ) : null}
      {section.kind === "rows" ? (
        <>
          <div className="ads-table-wrap">
            <table className="ads-table" data-testid="ads-table">
              <thead>
                <tr>
                  <th scope="col" className="ads-th-thumb">
                    <span className="ads-visually-hidden">Thumbnail</span>
                  </th>
                  <th scope="col">Ad</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="ads-th-num">
                    Impressions
                    <span className="ads-th-note">Last 30 days</span>
                  </th>
                  <th scope="col" className="ads-th-num">
                    Clicks
                    <span className="ads-th-note">Last 30 days</span>
                  </th>
                  <th scope="col" className="ads-th-num">
                    CTR
                    <span className="ads-th-note">Last 30 days</span>
                  </th>
                  <th scope="col">Preview</th>
                </tr>
              </thead>
              <tbody>
                {section.ads.map((ad) => (
                  <AdTableRow key={ad.id} ad={ad} now={now} />
                ))}
              </tbody>
            </table>
          </div>
          <p className="ads-muted ads-ad-footnote">
            Ad rows always cover the last 30 days and don&apos;t follow the date range above; the
            campaign totals remain the authoritative numbers.
          </p>
        </>
      ) : null}
    </section>
  );
}
